<template>
  <div class="relative overflow-hidden" :style="{ paddingBottom: currentPaddingBottom }">
    <div class="absolute inset-0 w-full h-full">
      <slot/>
    </div>
  </div>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import {debounce} from "lodash";
import {TAILWIND_LG, TAILWIND_MD, TAILWIND_SM, TAILWIND_XL, TAILWIND_XXL} from "../constants/Tailwind.js";

const props = defineProps({
  width: {
    type: Number,
    default: 16,
  },
  height: {
    type: Number,
    default: 9,
  },
  smWidth: Number,
  smHeight: Number,
  mdWidth: Number,
  mdHeight: Number,
  lgWidth: Number,
  lgHeight: Number,
  xlWidth: Number,
  xlHeight: Number,
  xxlWidth: Number,
  xxlHeight: Number,
});

const windowWidth = ref(window.innerWidth);
const updateWindowWidth = debounce(() => windowWidth.value = window.innerWidth, 100);

const breakpointPaddingBottom = (ratio) => `${ratio * 100}%`;

const currentPaddingBottom = computed(() => {
  const baseRatio = props.height / props.width;

  if (!hasMoreThanOneBreakpoint.value) return breakpointPaddingBottom(baseRatio);

  if (windowWidth.value >= TAILWIND_XXL && props.xxlWidth && props.xxlHeight) {
    return breakpointPaddingBottom(props.xxlHeight / props.xxlWidth);
  } else if (windowWidth.value >= TAILWIND_XL && props.xlWidth && props.xlHeight) {
    return breakpointPaddingBottom(props.xlHeight / props.xlWidth);
  } else if (windowWidth.value >= TAILWIND_LG && props.lgWidth && props.lgHeight) {
    return breakpointPaddingBottom(props.lgHeight / props.lgWidth);
  } else if (windowWidth.value >= TAILWIND_MD && props.mdWidth && props.mdHeight) {
    return breakpointPaddingBottom(props.mdHeight / props.mdWidth);
  } else if (windowWidth.value >= TAILWIND_SM && props.smWidth && props.smHeight) {
    return breakpointPaddingBottom(props.smHeight / props.smWidth);
  } else {
    return breakpointPaddingBottom(baseRatio);
  }
});

const hasMoreThanOneBreakpoint = computed(() => {
  return !!Object.keys(props).filter((key) =>
    key.endsWith("Width") &&
    props[key] !== null &&
    props[key] !== undefined &&
    props[key.replace("Width", "Height")] !== null &&
    props[key.replace("Width", "Height")] !== undefined
  ).length;
});

onMounted(() => {
  if (!hasMoreThanOneBreakpoint.value) return;
  window.addEventListener("resize", updateWindowWidth);
});

onBeforeUnmount(() => {
  if (!hasMoreThanOneBreakpoint.value) return;
  window.removeEventListener("resize", updateWindowWidth);
});
</script>
